// @flow

import React, { PureComponent } from 'react';
import Helmet from 'react-helmet';
import { Switch, Route, withRouter } from 'react-router';
import PropTypes from 'prop-types';
import Home from 'App/screens/Home';
import GMap from 'App/screens/GMap';
import SplatRouter from 'App/screens/SplatRouter';
import GlobalEvents from 'App/components/GlobalEvents';
import SvgSprite from 'App/components/SvgSprite';
import Header from 'App/components/Header';
import MobileMenu from 'App/components/MobileMenu';
import Wheel from 'App/components/Wheel';
import ScreenToggle from 'App/components/ScreenToggle';
import Share from 'App/components/Share';
import Subscribe from 'App/components/Subscribe';
import AliasToContentTypeQuery from 'App/components/Wheel/AliasToContentTypeQuery';

class App extends PureComponent {
  static defaultProps = {
    entities: [],
    apiUrl: '',
    gapiKey: '',
    location: {
      pathname: '',
      hash: '',
    },
  };

  static propTypes = {
    entities: PropTypes.arrayOf(PropTypes.any.isRequired),
    apiUrl: PropTypes.string.isRequired,
    gapiKey: PropTypes.string.isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired,
      hash: PropTypes.string.isRequired,
    }),
  };

  render() {
    const { entities, apiUrl, gapiKey, location } = this.props;
    const appNakedDomain = 'www.fergusonvoices.org';
    const appDomain = `https://${appNakedDomain}`;

    let bodyClasses = [];
    let isActOrStory = false;

    // The Map page is the only page that is known only by the React.js web app
    // and not the Drupal API. Also, the home page will show the wrong alias.
    if (location.pathname === '/map' || location.pathname === '/') {
      bodyClasses.push('js-no-scroll');
    } else {
      for (let i = 0; i < entities.length; i += 1) {
        if (location.pathname === entities[i].entityUrl.alias) {
          switch (entities[i].entityBundle) {
            case 'act_landing':
            case 'story':
              isActOrStory = true;
              break;
            default:
              break;
          }

          // Found the current page. Break out of the for loop.
          break;
        }
      }
    }

    if (location.pathname !== '/') {
      bodyClasses.push('js-no-intro-animation');
    }

    switch (location.hash) {
      case '#subscribe-to-podcast':
        bodyClasses.push('js-site-lightbox--open');
        bodyClasses.push('js-site-lightbox--subscribe');
        break;
      case '#share':
        bodyClasses.push('js-site-lightbox--open');
        bodyClasses.push('js-site-lightbox--share');
        break;
      case '#more-info':
        bodyClasses.push('js-more-info--open');
        break;
      default:
        break;
    }

    if (location.hash !== '#more-info' && isActOrStory) {
      bodyClasses.push('js-no-scroll');
      bodyClasses.push('js-more-info--closed');
    }

    bodyClasses = bodyClasses.join(' ');

    return (
      <div className="theme__bg">
        <Helmet
          titleTemplate="%s | Ferguson Voices"
          defaultTitle="Ferguson Voices"
        >
          <html lang="en" />
          <link rel="dns-prefetch" href="//ajax.googleapis.com" />
          <link rel="dns-prefetch" href="//fonts.googleapis.com" />
          <link rel="dns-prefetch" href="//use.typekit.net" />
          <link rel="dns-prefetch" href="//api.fergusonvoices.org" />
          <meta property="og:type" content="website" />
          <meta
            property="og:url"
            content={`${appDomain}${location.pathname}`}
          />
          <meta
            property="og:image"
            content={`${apiUrl}/themes/custom/fergusonvoices/images/ferguson-voices.jpg`}
          />
          <meta name="format-detection" content="telephone=no" />
          <meta name="google" content="notranslate" />
          <meta
            name="viewport"
            content="width=device-width,initial-scale=1,maximum-scale=1"
          />
          <link
            rel="apple-touch-icon"
            sizes="57x57"
            href={`${apiUrl}/themes/custom/fergusonvoices/images/icons/apple-touch-icon-57x57.png`}
          />
          <link
            rel="apple-touch-icon"
            sizes="60x60"
            href={`${apiUrl}/themes/custom/fergusonvoices/images/icons/apple-touch-icon-60x60.png`}
          />
          <link
            rel="apple-touch-icon"
            sizes="72x72"
            href={`${apiUrl}/themes/custom/fergusonvoices/images/icons/apple-touch-icon-72x72.png`}
          />
          <link
            rel="apple-touch-icon"
            sizes="76x76"
            href={`${apiUrl}/themes/custom/fergusonvoices/images/icons/apple-touch-icon-76x76.png`}
          />
          <link
            rel="apple-touch-icon"
            sizes="114x114"
            href={`${apiUrl}/themes/custom/fergusonvoices/images/icons/apple-touch-icon-114x114.png`}
          />
          <link
            rel="apple-touch-icon"
            sizes="120x120"
            href={`${apiUrl}/themes/custom/fergusonvoices/images/icons/apple-touch-icon-120x120.png`}
          />
          <link
            rel="apple-touch-icon"
            sizes="144x144"
            href={`${apiUrl}/themes/custom/fergusonvoices/images/icons/apple-touch-icon-144x144.png`}
          />
          <link
            rel="apple-touch-icon"
            sizes="152x152"
            href={`${apiUrl}/themes/custom/fergusonvoices/images/icons/apple-touch-icon-152x152.png`}
          />
          <link
            rel="apple-touch-icon"
            sizes="180x180"
            href={`${apiUrl}/themes/custom/fergusonvoices/images/icons/apple-touch-icon-180x180.png`}
          />
          <meta name="apple-mobile-web-app-capable" content="yes" />
          <meta
            name="apple-mobile-web-app-status-bar-style"
            content="black-translucent"
          />
          <meta name="apple-mobile-web-app-title" content="Ferguson Voices" />
          <link
            rel="icon"
            type="image/png"
            sizes="228x228"
            href={`${apiUrl}/themes/custom/fergusonvoices/images/icons/coast-228x228.png`}
          />
          <link
            rel="yandex-tableau-widget"
            href={`${apiUrl}/themes/custom/fergusonvoices/images/icons/yandex-browser-manifest.json`}
          />
          <link
            rel="icon"
            type="image/png"
            sizes="32x32"
            href={`${apiUrl}/themes/custom/fergusonvoices/images/icons/favicon-32x32.png`}
          />
          <link
            rel="icon"
            type="image/png"
            sizes="16x16"
            href={`${apiUrl}/themes/custom/fergusonvoices/images/icons/favicon-16x16.png`}
          />
          <link rel="shortcut icon" href="/favicon.ico" />
          <link
            rel="manifest"
            href={`${apiUrl}/themes/custom/fergusonvoices/images/icons/manifest.json`}
          />
          <meta name="mobile-web-app-capable" content="yes" />
          <meta name="theme-color" content="#1f3d49" />
          <meta name="application-name" content="Ferguson Voices" />
          <meta name="msapplication-TileColor" content="#1f3d49" />
          <meta
            name="msapplication-TileImage"
            content={`${apiUrl}/themes/custom/fergusonvoices/images/icons/mstile-144x144.png`}
          />
          <meta
            name="msapplication-config"
            content={`${apiUrl}/themes/custom/fergusonvoices/images/icons/browserconfig.xml`}
          />
          <link
            rel="apple-touch-startup-image"
            media="(device-width: 320px) and (device-height: 480px) and (-webkit-device-pixel-ratio: 1)"
            href={`${apiUrl}/themes/custom/fergusonvoices/images/icons/apple-touch-startup-image-320x460.png`}
          />
          <link
            rel="apple-touch-startup-image"
            media="(device-width: 320px) and (device-height: 480px) and (-webkit-device-pixel-ratio: 2)"
            href={`${apiUrl}/themes/custom/fergusonvoices/images/icons/apple-touch-startup-image-640x920.png`}
          />
          <link
            rel="apple-touch-startup-image"
            media="(device-width: 320px) and (device-height: 568px) and (-webkit-device-pixel-ratio: 2)"
            href={`${apiUrl}/themes/custom/fergusonvoices/images/icons/apple-touch-startup-image-640x1096.png`}
          />
          <link
            rel="apple-touch-startup-image"
            media="(device-width: 375px) and (device-height: 667px) and (-webkit-device-pixel-ratio: 2)"
            href={`${apiUrl}/themes/custom/fergusonvoices/images/icons/apple-touch-startup-image-750x1294.png`}
          />
          <link
            rel="apple-touch-startup-image"
            media="(device-width: 414px) and (device-height: 736px) and (orientation: landscape) and (-webkit-device-pixel-ratio: 3)"
            href={`${apiUrl}/themes/custom/fergusonvoices/images/icons/apple-touch-startup-image-1182x2208.png`}
          />
          <link
            rel="apple-touch-startup-image"
            media="(device-width: 414px) and (device-height: 736px) and (orientation: portrait) and (-webkit-device-pixel-ratio: 3)"
            href={`${apiUrl}/themes/custom/fergusonvoices/images/icons/apple-touch-startup-image-1242x2148.png`}
          />
          <link
            rel="apple-touch-startup-image"
            media="(device-width: 768px) and (device-height: 1024px) and (orientation: landscape) and (-webkit-device-pixel-ratio: 1)"
            href={`${apiUrl}/themes/custom/fergusonvoices/images/icons/apple-touch-startup-image-748x1024.png`}
          />
          <link
            rel="apple-touch-startup-image"
            media="(device-width: 768px) and (device-height: 1024px) and (orientation: portrait) and (-webkit-device-pixel-ratio: 1)"
            href={`${apiUrl}/themes/custom/fergusonvoices/images/icons/apple-touch-startup-image-768x1004.png`}
          />
          <link
            rel="apple-touch-startup-image"
            media="(device-width: 768px) and (device-height: 1024px) and (orientation: landscape) and (-webkit-device-pixel-ratio: 2)"
            href={`${apiUrl}/themes/custom/fergusonvoices/images/icons/apple-touch-startup-image-1496x2048.png`}
          />
          <link
            rel="apple-touch-startup-image"
            media="(device-width: 768px) and (device-height: 1024px) and (orientation: portrait) and (-webkit-device-pixel-ratio: 2)"
            href={`${apiUrl}/themes/custom/fergusonvoices/images/icons/apple-touch-startup-image-1536x2008.png`}
          />
          <style>{`
          html{line-height:1.15;-ms-text-size-adjust:100%;-webkit-text-size-adjust:100%}body{margin:0}article,aside,footer,header,nav,section{display:block}h1{font-size:2em;margin:.67em 0}figcaption,figure,main{display:block}figure{margin:1em 40px}hr{-webkit-box-sizing:content-box;box-sizing:content-box;height:0;overflow:visible}pre{font-family:monospace,monospace;font-size:1em}a{background-color:transparent;-webkit-text-decoration-skip:objects}abbr[title]{border-bottom:none;text-decoration:underline;text-decoration:underline dotted}b,strong{font-weight:inherit}b,strong{font-weight:bolder}code,kbd,samp{font-family:monospace,monospace;font-size:1em}dfn{font-style:italic}mark{background-color:#ff0;color:#000}small{font-size:80%}sub,sup{font-size:75%;line-height:0;position:relative;vertical-align:baseline}sub{bottom:-.25em}sup{top:-.5em}audio,video{display:inline-block}audio:not([controls]){display:none;height:0}img{border-style:none}svg:not(:root){overflow:hidden}button,input,optgroup,select,textarea{font-family:sans-serif;font-size:100%;line-height:1.15;margin:0}button,input{overflow:visible}button,select{text-transform:none}[type=reset],[type=submit],button,html [type=button]{-webkit-appearance:button}[type=button]::-moz-focus-inner,[type=reset]::-moz-focus-inner,[type=submit]::-moz-focus-inner,button::-moz-focus-inner{border-style:none;padding:0}[type=button]:-moz-focusring,[type=reset]:-moz-focusring,[type=submit]:-moz-focusring,button:-moz-focusring{outline:1px dotted ButtonText}fieldset{padding:.35em .75em .625em}legend{-webkit-box-sizing:border-box;box-sizing:border-box;color:inherit;display:table;max-width:100%;padding:0;white-space:normal}progress{display:inline-block;vertical-align:baseline}textarea{overflow:auto}[type=checkbox],[type=radio]{-webkit-box-sizing:border-box;box-sizing:border-box;padding:0}[type=number]::-webkit-inner-spin-button,[type=number]::-webkit-outer-spin-button{height:auto}[type=search]{-webkit-appearance:textfield;outline-offset:-2px}[type=search]::-webkit-search-cancel-button,[type=search]::-webkit-search-decoration{-webkit-appearance:none}::-webkit-file-upload-button{-webkit-appearance:button;font:inherit}details,menu{display:block}summary{display:list-item}canvas{display:inline-block}template{display:none}[hidden]{display:none}.ir{background-color:transparent;border:0;overflow:hidden;*text-indent:-9999px}.ir:before{content:"";display:block;width:0;height:150%}.hidden{display:none!important}.visually-hidden{border:0;clip:rect(0 0 0 0);height:1px;margin:-1px;overflow:hidden;padding:0;position:absolute;width:1px;white-space:nowrap}.visually-hidden.focusable:active,.visually-hidden.focusable:focus{clip:auto;height:auto;margin:0;overflow:visible;position:static;width:auto;white-space:inherit}.invisible{visibility:hidden}.clearfix:after,.clearfix:before{content:" ";display:table}.clearfix:after{clear:both}.site-app,[data-reactroot],body,html{height:100%}body{background-color:#353e4d}body.js-no-scroll{position:fixed;top:0;right:0;left:0}body.js-no-scroll.js-menu--open{position:relative;top:auto;right:auto;left:auto}body>svg{width:0;height:0;fill:#000}.list{margin:0;padding:0;list-style:none}.list__item{margin:0;padding:0}.center{position:absolute}.center--vertically{top:50%;-webkit-transform:translateY(-50%);transform:translateY(-50%)}.center--horizontally{left:50%;-webkit-transform:translateX(-50%);transform:translateX(-50%)}.center--both{top:50%;left:50%;-webkit-transform:translate(-50%,-50%);transform:translate(-50%,-50%)}.site-header{position:fixed;z-index:500;right:0;bottom:0;left:0;height:65px;padding-right:20px;padding-left:20px;overflow:visible;background-color:#353e4d}@media screen and (min-width:769px){.site-header{height:100px;padding-right:40px;padding-left:40px}}@media screen and (max-width:768px){.site-header{-webkit-transition:bottom .2s cubic-bezier(.25,.46,.45,.94);transition:bottom .2s cubic-bezier(.25,.46,.45,.94)}.js-scrolled .site-header{bottom:-100%}}.site-header__logo{width:107px;height:60.9076923px;margin-top:-15px;float:left}@media screen and (min-width:769px){.site-header__logo{width:195px;height:111px;margin-top:-40px}}.site-header__sponsors{display:none;padding:14px 0 0 18px;float:left;color:#fff}@media screen and (min-width:769px){.site-header__sponsors{display:block}}@media screen and (min-width:1031px){.site-header__sponsors{display:-webkit-box;display:-ms-flexbox;display:flex;padding-top:28px}}.site-header__sponsors-title{margin:10px 0;padding-right:18px}.site-header__sponsors-title.h3--tiny{line-height:30px}.site-header__sponsors-list-item{float:left}.site-header__sponsors-list-item:not(:first-child){padding-left:9px}.site-header__sponsors-list-item:not(:last-child){padding-right:9px}.site-header__navigation{height:65px;float:right;color:#acacac}@media screen and (min-width:769px){.site-header__navigation{height:100px}}.site-header__navigation-list{position:relative;overflow:hidden}.site-header__navigation-list-item{float:left}.site-header__navigation-list-item:not(:first-child){padding-left:24px}.site-header__navigation-list-item:not(:last-child){padding-right:24px;border-right:2px solid #049fc0}body{-moz-osx-font-smoothing:grayscale;-webkit-font-smoothing:antialiased}html{font-size:62.5%}body{font-family:Yantramanav,Helvetica,Arial,sans-serif;text-rendering:optimizeLegibility;-webkit-font-kerning:normal;font-kerning:normal;-webkit-font-variant-ligatures:common-ligatures,contextual;font-variant-ligatures:common-ligatures,contextual;-webkit-font-feature-settings:"kern","liga","clig","calt";font-feature-settings:"kern","liga","clig","calt"}svg{width:100%;height:100%;fill:currentColor}.svg-wrapper{display:inline-block;vertical-align:middle}.svg-wrapper.center{display:block}.svg-wrapper--left-double-quote{width:24px;height:16px}.svg-wrapper--left-small-double-quote{width:20px;height:13.3333333px}.svg{display:block}.svg--sponsor-proof{width:70px;height:28px}.svg--sponsor-u-of-dayton{width:64px;height:32px}.icon{width:38px;height:34px}.icon--tiny{width:20px;height:17.8947368px}.icon--small{width:30px;height:26.8421053px}.icon-grid{width:195px;margin:0 auto 2em;text-align:left}.icon-grid__item{display:inline-block;margin:0 13.5px 15px}.site-content,.site-controls,.site-lightbox,.site-menu,.site-toggle{display:none}
          `}</style>
          <script type="application/ld+json">{`
          {
            "@context":"http://schema.org",
            "@type":"Organization",
            "legalName":"Ferguson Voices",
            "url":"${appDomain}",
            "logo":"${apiUrl}/themes/custom/fergusonvoices/logo.jpg"
          }
          `}</script>
          <body className={bodyClasses} />
        </Helmet>
        <SvgSprite />
        <Header />
        <MobileMenu />
        <Wheel />
        <ScreenToggle />
        <Switch>
          <Route exact path="/" component={Home} />
          <Route
            exact
            path="/map"
            render={props => <GMap {...props} gapiKey={gapiKey} />}
          />
          <Route exact path="*" component={SplatRouter} />
        </Switch>
        <div className="site-lightbox">
          <Share appDomain={appDomain} appNakedDomain={appNakedDomain} />
          <Subscribe />
        </div>
        <GlobalEvents />
      </div>
    );
  }
}

export default AliasToContentTypeQuery(withRouter(App));



// WEBPACK FOOTER //
// ./screens/App/index.js